// Einstellungen → Module: Übersicht der Module dieses Mandanten aus der EINZIGEN
// Quelle (Super-Admin-gebundenes Abo/Modul-Gating, GET /settings/enabled-modules).
// Zeigt Preis & Buchungsstatus; Ein-/Ausschalten läuft über das Abo. Pro gebuchtem,
// kundensichtbarem Modul kann der Betrieb festlegen, ob es Endkunden in der App
// angezeigt wird (Opt-in, Standard aus) und – per Drag-and-drop – in welcher
// Reihenfolge die sichtbaren Module in der App erscheinen.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Loader2, Blocks, CheckCircle2, Lock, ArrowRight, Smartphone, GripVertical, Eye } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortableModule = ({ id, name }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`module-order-item-${id}`}
      className="flex items-center gap-3 rounded-lg border bg-background px-3 py-2.5"
    >
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
        data-testid={`module-order-handle-${id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-sm font-medium">{name}</span>
    </div>
  );
};

const ModulesSettingsPage = ({ embedded = false }) => {
  const [catalog, setCatalog] = useState({});
  const [enabled, setEnabled] = useState([]);
  const [appVisible, setAppVisible] = useState([]);
  const [order, setOrder] = useState([]);
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/enabled-modules`);
        setCatalog(res.data?.catalog || {});
        setEnabled(res.data?.enabled || []);
        setAppVisible(res.data?.app_visible || []);
        setOrder(res.data?.app_order || []);
      } catch {
        toast.error('Module konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleAppVisible = async (key, next) => {
    const updated = next
      ? Array.from(new Set([...appVisible, key]))
      : appVisible.filter((k) => k !== key);
    const prev = appVisible;
    setAppVisible(updated);
    setSaving(key);
    try {
      const res = await axios.put(`${API}/settings/customer-visible-modules`, { modules: updated });
      setAppVisible(res.data?.app_visible || updated);
      toast.success(next ? 'In der Kunden-App sichtbar' : 'Aus der Kunden-App ausgeblendet');
    } catch {
      setAppVisible(prev);
      toast.error('Konnte nicht gespeichert werden');
    } finally {
      setSaving(null);
    }
  };

  const saveOrder = async (next) => {
    const prev = order;
    setOrder(next);
    try {
      await axios.put(`${API}/settings/customer-module-order`, { order: next });
    } catch {
      setOrder(prev);
      toast.error('Reihenfolge konnte nicht gespeichert werden');
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>;

  const visibleEntries = Object.entries(catalog).filter(([, m]) => m.customer_visible);
  const enabledSet = new Set(enabled);
  const appVisibleSet = new Set(appVisible);
  const activeVisibleCount = visibleEntries.filter(([key]) => enabledSet.has(key)).length;
  const customerShownCount = visibleEntries.filter(([key]) => enabledSet.has(key) && appVisibleSet.has(key)).length;

  const activeVisibleKeys = visibleEntries
    .filter(([key]) => enabledSet.has(key) && appVisibleSet.has(key))
    .map(([key]) => key);
  const orderedVisible = [
    ...order.filter((k) => activeVisibleKeys.includes(k)),
    ...activeVisibleKeys.filter((k) => !order.includes(k)),
  ];

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = orderedVisible.indexOf(active.id);
    const newIndex = orderedVisible.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    saveOrder(arrayMove(orderedVisible, oldIndex, newIndex));
  };

  return (
    <div className="space-y-5" data-testid="modules-settings">
      {!embedded && (
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-sky-100 text-sky-600"><Blocks className="w-5 h-5" /></div>
          <div>
            <h2 className="text-lg font-semibold">Module</h2>
            <p className="text-sm text-muted-foreground">Welche Funktionen für dieses Studio gebucht sind. Pro gebuchtem Modul legst du mit dem Schalter fest, ob es deinen Endkunden in der App angezeigt wird (Standard: aus); die Reihenfolge ordnest du per Drag-and-drop. Dazubuchen über dein Abo.</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
        <div className="space-y-0.5">
          <p className="text-sm"><span className="font-semibold">{activeVisibleCount}</span> von {visibleEntries.length} Modulen aktiv</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="customer-visible-count">
            <Eye className="w-3.5 h-3.5" />
            {customerShownCount === 0
              ? 'Deine Kunden sehen aktuell keine Module in der App'
              : <span><span className="font-semibold text-foreground">{customerShownCount}</span> {customerShownCount === 1 ? 'Modul ist' : 'Module sind'} für deine Kunden in der App sichtbar</span>}
          </p>
        </div>
        <Button onClick={() => navigate('/admin/subscription')} data-testid="modules-manage-btn">
          Module verwalten / dazubuchen <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {orderedVisible.length > 1 && (
        <Card data-testid="module-order-card">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-muted-foreground" />
              <p className="font-medium text-sm">Reihenfolge in der Kunden-App</p>
            </div>
            <p className="text-xs text-muted-foreground">Ziehe die Module in die Reihenfolge, in der sie deinen Kunden angezeigt werden sollen.</p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={orderedVisible} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {orderedVisible.map((key) => (
                    <SortableModule key={key} id={key} name={catalog[key]?.name || key} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {visibleEntries.map(([key, m]) => {
          const active = enabledSet.has(key);
          const shown = appVisibleSet.has(key);
          return (
            <Card key={key} className={active ? '' : 'opacity-70'} data-testid={`module-card-${key}`}>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {m.price_chf > 0 ? `CHF ${m.price_chf}/Monat` : 'inklusive'}
                    </p>
                  </div>
                  {active ? (
                    <Badge className="bg-green-100 text-green-700 border-0 gap-1" data-testid={`module-status-${key}`}><CheckCircle2 className="w-3 h-3" /> Aktiv</Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1 text-muted-foreground" data-testid={`module-status-${key}`}><Lock className="w-3 h-3" /> Nicht gebucht</Badge>
                  )}
                </div>
                {active && (
                  <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Smartphone className="w-3.5 h-3.5" /> In Kunden-App anzeigen
                    </span>
                    <Switch
                      checked={shown}
                      disabled={saving === key}
                      onCheckedChange={(v) => toggleAppVisible(key, v)}
                      data-testid={`module-appvisible-${key}`}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ModulesSettingsPage;
